import loadable from '@loadable/component';
import React, { useEffect, useRef, useState } from 'react';

import useClassy from '@core/hooks/useClassy';
import Graphic from '@ui/Graphic';
import Modal, { ModalHeader } from '@ui/Modal';
import Title from '@ui/Title';

import { hydrationDebugData } from './const';
import classes from './index.module.scss';
import { formatHtml, isDebugHydrationEnabled } from './util';
const SplitDiff = loadable(() => import('./SplitDiff'), { ssr: false });

/**
 * Shows a side-by-side comparison of the server-rendered HTML and the
 * client-rendered HTML when a hydration mismatch occurs. This is useful
 * for debugging hydration issues in development.
 *
 * To use, add `?debugHydration` to the URL in a non-production environment.
 */
export default function HydrationDebugger({ children }: { children?: React.ReactNode }) {
  const modalRef = useRef<Modal>(null);
  const bem = useClassy(classes, 'HydrationDebugger');
  const [isEnabled, setIsEnabled] = useState(false);
  const [ssrHtml, setSsrHtml] = useState('');
  const [csrHtml, setCsrHtml] = useState('');

  useEffect(() => {
    setIsEnabled(isDebugHydrationEnabled());
  }, []);

  useEffect(() => {
    if (!isEnabled || !window[hydrationDebugData]?.error) return;
    const { ssrHtml: ssr, csrHtml: csr } = window[hydrationDebugData];
    setSsrHtml(formatHtml(ssr));
    setCsrHtml(formatHtml(csr));
    modalRef.current?.toggle('open');
  }, [isEnabled]);

  return (
    <>
      {!!isEnabled && (
        <>
          <Modal
            ref={modalRef}
            className={bem('&')}
            data-color-mode="dark"
            size="md"
            target="#hydration-debugger-target"
            verticalCenter
          >
            <ModalHeader toggleClose={() => modalRef.current?.toggle('close')}>
              <Title as={2} className={bem('-header')} level={4}>
                <Graphic name="warning" />
                HTML Diff
              </Title>
            </ModalHeader>
            <div className={bem('-body')}>
              <SplitDiff
                leftTitle="Server Side Render"
                newText={csrHtml}
                oldText={ssrHtml}
                rightTitle="Client Side Render"
              />
            </div>
          </Modal>
          <div className="ModalWrapper" id="hydration-debugger-target" />
        </>
      )}
      {children}
    </>
  );
}

export { debugHydrateRootHandler } from './debugHydrateRootHandler';
export { isDebugHydrationEnabled } from './util';
